import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import {
  TemplateTextTypeModel,
  templateTextTypeApi,
} from "../api/TemplateTextTypeApi";
import LoadingDisplay from "../common/LoadingDisplay";
import { drawingApi } from "../api/drawingApi";

interface ImportDxfModalProps {
  vehicleFileNumber: string;
  onClose: () => void;
  onImport: (warnings: string[]) => void;
}

function ImportDxfModal({
  vehicleFileNumber,
  onClose,
  onImport,
}: ImportDxfModalProps) {
  const [error, setError] = useState<Problem | string | undefined>();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<string[]>([]);
  const [templateTextTypes, setTemplateTextTypes] = useState<
    TemplateTextTypeModel[]
  >([]);
  const [working, setWorking] = useState(false);
  const [dxfFile, setDxfFile] = useState<File | undefined>(undefined);

  const [selectedTemplate, setSelectedTemplate] = useState<string>();

  const [selectedTextTypeId, setSelectedTextTypeId] = useState<
    number | undefined
  >();

  const [tryToKeepOrder, setTryToKeepOrder] = useState(true);

  const [reset, setReset] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);

      // load templates
      const templateResult = await drawingApi.getDrawingTemplateList();

      if (templateResult.ok) {
        // load templateTextTypes
        const templateTextTypesResult =
          await templateTextTypeApi.getTemplateTextTypes();

        if (templateTextTypesResult.ok) {
          setError(undefined);

          setTemplates(templateResult.value);
          setSelectedTemplate(
            templateResult.value[templateResult.value.length - 1],
          );
          setTemplateTextTypes(templateTextTypesResult.value);
          setSelectedTextTypeId(templateTextTypesResult.value[0]?.id);
        } else {
          setError(templateTextTypesResult.problem);
        }
      } else {
        setError(templateResult.problem);
      }

      setLoading(false);
    }
    load();
  }, []);

  const handleImportDxf = async () => {
    if (!dxfFile) {
      setError("No .dxf file loaded");
      return;
    } else if (selectedTextTypeId === undefined || selectedTextTypeId < 0) {
      setError("Template text type not set.");
      return;
    }

    setWorking(true);
    let response = await drawingApi.importDxf(
      vehicleFileNumber,
      selectedTemplate,
      selectedTextTypeId,
      reset,
      tryToKeepOrder,
      await dxfFile.text(),
    );
    setWorking(false);

    if (response.ok) {
      const [warnings] = response.value;

      setError(undefined);

      onImport(warnings);
    } else {
      setError(response.problem);
    }
  };

  return (
    <Modal
      show={true}
      onHide={() => {
        onClose();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Import AutoCAD .dxf file</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorDisplay error={error} />
        <LoadingDisplay loading={loading} />
        {!loading && (
          <>
            <Row className="mb-2">
              <Form.Label column htmlFor="import-dxg-template">
                Drawing template
              </Form.Label>
              <Col>
                <Form.Select
                  id="import-dxg-template"
                  defaultValue={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  <option value="">&lt;default&gt;</option>
                  {templates.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mb-2">
              <Form.Label column htmlFor="import-dxf-type">
                Text template
              </Form.Label>
              <Col>
                <Form.Select
                  id="import-dxf-type"
                  defaultValue={
                    templateTextTypes.length > 0
                      ? templateTextTypes[0].id
                      : undefined
                  }
                  onChange={(e) =>
                    setSelectedTextTypeId(Number(e.target.value))
                  }
                >
                  {templateTextTypes.map((ttt, index) => (
                    <option key={ttt.id} value={ttt.id}>
                      {ttt.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col>
                <Form.Check
                  id="import-dxf-reset-drawing"
                  label="Reset Drawing"
                  defaultChecked={reset}
                  onChange={(e) => setReset(e.target.checked)}
                />
              </Col>
              <Col>
                <Form.Check
                  id="import-dxf-keep-order"
                  label="Try to keep order"
                  defaultChecked={true}
                  onChange={(e) => setTryToKeepOrder(e.target.checked)}
                />
              </Col>
            </Row>
            <Form.Group>
              <input
                className="form-control"
                type="file"
                accept=".dxf"
                onChange={(e) =>
                  e.target.files && setDxfFile(e.target.files[0])
                }
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          size="sm"
          disabled={!dxfFile || working}
          onClick={() => handleImportDxf()}
        >
          Import
        </Button>
        <Button variant="secondary" size="sm" onClick={() => onClose()}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportDxfModal;
